import * as React from "react"
import { ThemeCss } from "gatsby-interface"
import { PlanTierSelector } from "./PlanTierSelector"
import {
  BillingInterval,
  MachinePricingTier,
  SubscriptionPlan,
} from "@modules/graphql/types"
import { PlanInfo } from "@modules/billing/shared/constants/tiers"

const rootCss: ThemeCss = theme => ({
  display: `grid`,
  gridGap: theme.space[9],
})

export const PricingTier = {
  Free: MachinePricingTier.Free,
  Standard: MachinePricingTier.Standard,
  Performance: MachinePricingTier.Performance,
  Enterprise: MachinePricingTier.Enterprise,
}

export type MultiPlanSelectorProps = {
  planInfo: PlanInfo
  setPlanInfo: (val: PlanInfo) => void
  availablePlans?: SubscriptionPlan[]
  diyTiersOnly?: boolean
  showDetailsLinks?: boolean
  contactSalesLink?: string
  showIntervalToggle?: boolean
  enterpriseSelectable?: boolean
}

export function MultiPlanSelector({
  planInfo,
  setPlanInfo,
  availablePlans,
  diyTiersOnly = false,
  showDetailsLinks = false,
  contactSalesLink = `/contact-us/`,
  showIntervalToggle = true,
  enterpriseSelectable = false,
  ...rest
}: MultiPlanSelectorProps) {
  const [buildsTier, setBuildsTier] = React.useState<
    MachinePricingTier | null | undefined
  >(null)
  const [hostingTier, setHostingTier] = React.useState<
    MachinePricingTier | null | undefined
  >(null)
  const [billingInterval, setBillingInterval] = React.useState(
    BillingInterval.Annual
  )

  React.useEffect(() => {
    setBuildsTier(planInfo.buildsTier)
    setHostingTier(planInfo.hostingTier)
    setBillingInterval(planInfo.billingInterval)
  }, [])

  React.useEffect(() => {
    if (buildsTier && planInfo.buildsTier !== buildsTier) {
      setPlanInfo({
        ...planInfo,
        buildsTier,
      })

      if (
        hostingTier === null ||
        buildsTier === MachinePricingTier.Enterprise ||
        hostingTier === MachinePricingTier.Enterprise
      ) {
        setHostingTier(buildsTier)
      }
    }

    if (hostingTier && planInfo.hostingTier !== hostingTier) {
      setPlanInfo({
        ...planInfo,
        hostingTier,
      })

      if (
        buildsTier === null ||
        hostingTier === MachinePricingTier.Enterprise ||
        buildsTier === MachinePricingTier.Enterprise
      ) {
        setBuildsTier(hostingTier)
      }
    }

    if (planInfo.billingInterval !== billingInterval) {
      setPlanInfo({
        ...planInfo,
        billingInterval,
      })
    }

    if (availablePlans) {
      const newPlan = availablePlans?.find(plan => {
        if (plan.name === `Free`) {
          return (
            plan.buildsTier === buildsTier && plan.hostingTier === hostingTier
          )
        } else {
          return (
            plan.buildsTier === buildsTier &&
            plan.hostingTier === hostingTier &&
            plan.interval === billingInterval
          )
        }
      })

      if (newPlan && newPlan.id !== planInfo.planId) {
        setPlanInfo({
          ...planInfo,
          planId: newPlan.id,
          planName: newPlan.name,
        })
      }
    }
  }, [buildsTier, hostingTier, billingInterval, planInfo, availablePlans])

  const supportedTiers = [
    MachinePricingTier.Free,
    MachinePricingTier.Standard,
    MachinePricingTier.Performance,
    MachinePricingTier.Enterprise,
  ]

  const presentedTiers = diyTiersOnly
    ? supportedTiers.filter(tier => tier !== MachinePricingTier.Enterprise)
    : supportedTiers

  return (
    <div css={rootCss} {...rest}>
      <PlanTierSelector
        tierType="builds"
        selectedTier={buildsTier}
        tiers={presentedTiers}
        setTier={setBuildsTier}
        billingInterval={billingInterval}
        setBillingInterval={setBillingInterval}
        contactSalesLink={contactSalesLink}
        showDetailsLinks={showDetailsLinks}
        showIntervalToggle={showIntervalToggle}
        enterpriseSelectable={enterpriseSelectable}
      />
      <PlanTierSelector
        tierType="hosting"
        selectedTier={hostingTier}
        tiers={presentedTiers}
        setTier={setHostingTier}
        billingInterval={billingInterval}
        setBillingInterval={setBillingInterval}
        showIntervalToggle={false}
        contactSalesLink={contactSalesLink}
        showDetailsLinks={showDetailsLinks}
        enterpriseSelectable={enterpriseSelectable}
      />
    </div>
  )
}
